// Printable / downloadable site-visit report for one sub-task, built in the browser.
import dayjs from 'dayjs';
import { engineerName, fmtDuration, fmtRange, fmtTime, visitMinutes } from '../projectSchedule.utils';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Photo URLs are signed for an hour, so a downloaded file embeds the images. If the bucket blocks
// cross-origin reads, fall back to the signed URL (fine for printing straight away).
const toDataUrl = async (url) => {
    try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) return url;
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(url);
            reader.readAsDataURL(blob);
        });
    } catch {
        return url;
    }
};

const STYLES = `body{font:14px/1.5 Arial,sans-serif;color:#1f2933;max-width:820px;margin:24px auto;padding:0 20px}
.hd{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #008080;padding-bottom:10px;gap:16px}
.hd h1{margin:0;font-size:22px;color:#008080}.hd small{color:#6b7785;text-align:right}
table{width:100%;border-collapse:collapse;margin:14px 0}td,th{border:1px solid #dfe4e8;padding:7px 9px;text-align:left}th{background:#f4f6f8;font-size:12px;text-transform:uppercase}
.day{border:1px solid #dfe4e8;border-radius:8px;padding:12px 14px;margin:14px 0;page-break-inside:avoid}.day h3{margin:0 0 6px;font-size:15px}
.k{font-size:11px;text-transform:uppercase;color:#6b7785;font-weight:bold;margin-top:8px}
.ph{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:8px}.ph img{width:100%;height:150px;object-fit:cover;border-radius:6px;border:1px solid #dfe4e8}
.sig{display:flex;gap:40px;margin-top:40px}.sig div{flex:1;border-top:1px solid #999;padding-top:6px;font-size:12px;color:#6b7785}
.part{page-break-before:always;margin-top:28px}.part:first-of-type{page-break-before:auto}.part h2{color:#008080;font-size:17px;margin:0 0 4px}`;

const header = (title, subtitle) => `<div class="hd"><div><h1>${esc(title)}</h1><b>${esc(subtitle)}</b></div><small>Greenvironment Innovation &amp; Marketing India (P) Ltd<br>Generated ${esc(dayjs().format('DD/MM/YYYY h:mm a'))}</small></div>`;

// One piece of work: summary table, visit times, then each day's report
const taskSection = (task, visits) => {
    const total = visits.reduce((s, v) => s + visitMinutes(v), 0);
    return `<table><tr><th>Site</th><td>${esc(task.facility_name)}</td><th>Engineer</th><td>${esc(engineerName(task))}</td></tr>
<tr><th>Scheduled</th><td>${esc(fmtRange(task.start_date, task.end_date, 'DD/MM/YYYY'))}</td><th>Status</th><td>${esc(task.status)}</td></tr>
<tr><th>Visits</th><td>${visits.length}</td><th>Total time on site</th><td>${fmtDuration(total)}</td></tr></table>
${visits.length ? `<table><tr><th>Date</th><th>Engineer</th><th>Check in</th><th>Check out</th><th>Hours</th></tr>${visits.map(v => `<tr><td>${esc(dayjs(v.visit_date).format('ddd DD/MM/YYYY'))}</td><td>${esc(v.user_name)}</td><td>${fmtTime(v.check_in_at)}</td><td>${fmtTime(v.check_out_at)}</td><td>${fmtDuration(visitMinutes(v))}</td></tr>`).join('')}</table>` : '<p>No completed visits yet.</p>'}
${visits.map(v => `<div class="day"><h3>${esc(dayjs(v.visit_date).format('dddd D MMMM YYYY'))} · ${fmtTime(v.check_in_at)} – ${fmtTime(v.check_out_at)} · ${esc(v.user_name)}</h3>
<div class="k">Work done</div><div>${esc(v.notes || '—')}</div>
${v.issues ? `<div class="k">Issues / observations</div><div>${esc(v.issues)}</div>` : ''}
${v.next_steps ? `<div class="k">Next steps</div><div>${esc(v.next_steps)}</div>` : ''}
${v.client_rep ? `<div class="k">Client representative</div><div>${esc(v.client_rep)}</div>` : ''}
${v.photos?.length ? `<div class="k">Photos</div><div class="ph">${v.photos.map(p => `<img src="${esc(p.url)}" alt="Site photo">`).join('')}</div>` : ''}</div>`).join('')}`;
};

const SIGNATURES = '<div class="sig"><div>Engineer signature</div><div>Client representative signature</div></div>';

const buildHtml = (task, visits, title) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)} – ${esc(task.sub_task_name)}</title>
<style>${STYLES}</style></head><body>${header(title, task.sub_task_name)}${taskSection(task, visits)}${SIGNATURES}</body></html>`;

const reportTitle = (onlyDay) => (onlyDay ? `Site visit report – ${dayjs(onlyDay).format('D MMM YYYY')}` : 'Site visit report');

// Only checked-out visits belong in a report
const reportVisits = (visits, onlyDay) => visits
    .filter(v => v.check_out_at && (!onlyDay || v.visit_date === onlyDay))
    .sort((a, b) => a.visit_date.localeCompare(b.visit_date));

export async function downloadVisitReport(task, visits, onlyDay) {
    const selected = reportVisits(visits, onlyDay);
    const embedded = await Promise.all(selected.map(async v => ({
        ...v,
        photos: await Promise.all((v.photos || []).map(async p => ({ ...p, url: await toDataUrl(p.url) }))),
    })));
    const html = buildHtml(task, embedded, reportTitle(onlyDay));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    a.download = `Site-visit-report_${task.facility_name}_${task.sub_task_name}${onlyDay ? `_${onlyDay}` : ''}.html`.replace(/[^\w.-]+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function printVisitReport(task, visits, onlyDay) {
    const w = window.open('', '_blank');
    if (!w) return downloadVisitReport(task, visits, onlyDay);
    w.document.write(buildHtml(task, reportVisits(visits, onlyDay), reportTitle(onlyDay)));
    w.document.close();
    setTimeout(() => w.print(), 600);
}

const saveHtml = (html, fileName) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    a.download = fileName.replace(/[^\w.-]+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// Every checked-out visit of a project in one file: parts = [{ task, visits }], one section per piece of work
export async function downloadProjectVisitsReport(projectTitle, parts) {
    const withVisits = parts.map(p => ({ ...p, visits: reportVisits(p.visits) })).filter(p => p.visits.length);
    const embedded = await Promise.all(withVisits.map(async p => ({
        ...p,
        visits: await Promise.all(p.visits.map(async v => ({ ...v, photos: await Promise.all((v.photos || []).map(async ph => ({ ...ph, url: await toDataUrl(ph.url) }))) }))),
    })));
    const total = embedded.reduce((n, p) => n + p.visits.length, 0);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Site visit reports – ${esc(projectTitle)}</title><style>${STYLES}</style></head><body>
${header('Site visit reports', projectTitle)}<p>${total} visit${total === 1 ? '' : 's'} across ${embedded.length} piece${embedded.length === 1 ? '' : 's'} of work.</p>
${embedded.map(p => `<div class="part"><h2>${esc(p.task.sub_task_name)}</h2>${taskSection(p.task, p.visits)}</div>`).join('') || '<p>No completed visits yet.</p>'}
${SIGNATURES}</body></html>`;
    saveHtml(html, `Site-visit-reports_${projectTitle}.html`);
    return total;
}
